import * as React from 'react';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import Modal from 'react-bootstrap/Modal';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';
import { useNavigate } from 'react-router-dom';
import type { GenericList } from './models/JobSharedInterfaces';

interface AddPublicHoliday {
    name: string;
    holidayTypeId: number;
    specificDate: string;
    day: number;
    month: number;
    week: number;
    subId: string;
}

interface PublicHolidayRules {
    id: number;
    type: number;
    name: string;
    day: number;
    month: number;
    year: number;
    week: number;
}

interface PublicHolidayDates {
    name: string;
    date: string;
    dateDisplay: string;
}

const PublicHolidayData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(false);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [showAddPublicHoliday, setShowAddPublicHoliday] = React.useState(false);
    var initPublicHoliday: AddPublicHoliday = {
        name: "",
        holidayTypeId: -1,
        specificDate: "",
        day: 0,
        month: 0,
        week: 0,
        subId: ""
    };
    const [addPublicHoliday, setAddPublicHoliday] = React.useState<AddPublicHoliday>(initPublicHoliday);
    const [holidayTypes, setHolidayTypes] = React.useState<GenericList[]>([]);
    const [holidayRules, setHolidayRules] = React.useState<PublicHolidayRules[]>([]);
    const [startYear, setStartYear] = React.useState(2026);
    const [endYear, setEndYear] = React.useState(2026);
    const [holidayDates, setHolidayDates] = React.useState<PublicHolidayDates[]>([]);
    const [holidayYears, setHolidayYears] = React.useState<number[]>([]);
    const [deleteHolidayId, setDeleteHolidayId] = React.useState(0);
    const [deleteHolidayIndex, setDeleteHolidayIndex] = React.useState(-1);
    const [deleteHolidayName, setDeleteHolidayName] = React.useState("");
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        //holiday years
        var today = new Date();
        var years: number[] = [];
        for (var i = 0; i < 5; i++) {
            years.push(today.getFullYear() + i);
        }
        setHolidayYears(years);
        setStartYear(today.getFullYear());
        setEndYear(today.getFullYear());

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("CreateEditAdminLists");
        if (hasPermission) {
            getPublicHolidays();
        } else {
            navigate("/accessdenied");
        }
    }

    const getPublicHolidays = async () => {
        const apiUrl = "GetPublicHolidayData";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setHolidayTypes(result.data.holidayTypes);
            setHolidayRules(result.data.holidayRules);
            setLoading(false);
        }
    }

    const showAddNewPublicHoliday = () => {
        setShowAddPublicHoliday(!showAddPublicHoliday);
    }

    const clearError = (errorItem: string) => {
        let errorList = errors;
        delete errorList[errorItem];

        setErrors(errorList);
    }

    const handleAddChange = (e: any) => {
        clearError(e.target.name);

        setAddPublicHoliday(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleAddTypeChange = (e: any) => {
        clearError(e.target.name);

        setAddPublicHoliday(prevState => ({
            ...prevState,
            holidayTypeId: e.target.value * 1,
            day: 0,
            month: 0,
            week: 0,
            specificDate: ""
        }));
    }

    const validateAdd = () => {
        let formIsValid = true;
        let errorList: any = {};

        if (!addPublicHoliday.name) {
            errorList["name"] = "Name is Required";
            formIsValid = false;
        }

        if (addPublicHoliday.holidayTypeId < 1) {
            errorList["holidayTypeId"] = "Type is Required";
            formIsValid = false;
        } else {
            if (addPublicHoliday.holidayTypeId == 1 && !addPublicHoliday.specificDate) {
                errorList["specificDate"] = "Specific Date is Required";
                formIsValid = false;
            } else if (addPublicHoliday.holidayTypeId == 1 && !DateHelpers.isValidDate(addPublicHoliday.specificDate)) {
                errorList["specificDate"] = "Valid Specific Date is Required";
                formIsValid = false;
            }
            if (addPublicHoliday.holidayTypeId > 1 && addPublicHoliday.holidayTypeId < 5 && (addPublicHoliday.day < 1 || addPublicHoliday.day > 31)) {
                errorList["day"] = "Day between 1 and 31 is Required";
                formIsValid = false;
            }
            if (addPublicHoliday.holidayTypeId == 5 && (addPublicHoliday.day < -2 || addPublicHoliday.day > 1)) {
                //Easter Day Rule
                errorList["day"] = "Day between -2 and 1 is Required";
                formIsValid = false;
            }
            if (addPublicHoliday.holidayTypeId > 1 && addPublicHoliday.holidayTypeId < 5 && (addPublicHoliday.month < 1 || addPublicHoliday.month > 12 )) {
                errorList["month"] = "Month between 1 and 12 is Required";
                formIsValid = false;
            }
            if (addPublicHoliday.holidayTypeId == 4 && (addPublicHoliday.week < 1 || addPublicHoliday.week > 5)) {
                errorList["week"] = "Week between 1 and 5 is Required";
                formIsValid = false;
            }

        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = () => {
        if (validateAdd()) {
            savePublicHolidays();
        }
    }

    const savePublicHolidays = async () => {
        var jsonItem = JSON.stringify(addPublicHoliday);

        const apiUrl = "SavePublicHoliday";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Public Holiday Saved");
            //then need to clear holiday so can save a new one
            var holiday: AddPublicHoliday = {
                name: "",
                holidayTypeId: -1,
                specificDate: "",
                day: 0,
                month: 0,
                week: 0,
                subId: ""
            };

            setAddPublicHoliday(holiday);
            setShowAddPublicHoliday(false);
            getPublicHolidays();
        };
    }

    const showPublicHolidays = () => {
        showPublicHols();
    }

    const showPublicHols = async () => {
        const apiUrl = "GetPublicHolidays?startYear=" + startYear + '&endYear=' + endYear;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setHolidayDates(result.data);
        }
    }

    const deleteItem = (e: any, delHolidayId: number, delHolidayIndex: number, delHolidayName: string) => {
        e.preventDefault();
        setDeleteHolidayId(delHolidayId);
        setDeleteHolidayIndex(delHolidayIndex);
        setDeleteHolidayName(delHolidayName);
        setShowConfirmModal(true);
    }

    const hideConfirmModal = () => {
        setDeleteHolidayId(0);
        setDeleteHolidayIndex(0);
        setDeleteHolidayName("");
        setShowConfirmModal(false);
    }

    const saveConfirmModal = () => {
        deletePublicHols();
    }

    const deletePublicHols = async () => {
        var holRules = [...holidayRules];
        var publicHoliday = {
            publicHolidayId: deleteHolidayId,
        };

        var jsonItem = JSON.stringify(publicHoliday);

        const apiUrl = "DeletePublicHoliday";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Public Holiday Deleted Successfully");
            
            holRules.splice(deleteHolidayIndex, 1);
            setHolidayRules(holRules);
            setDeleteHolidayId(0);
            setDeleteHolidayIndex(0);
            setDeleteHolidayName("");
            setShowConfirmModal(false);
        };
    }

    const renderDetails = (
        <div>
            <div className="static-modal">
                <Modal size="sm" show={showConfirmModal} onHide={hideConfirmModal}>
                    <Modal.Header className="modal__header--colour">
                        <Modal.Title>Delete Holiday/Rule</Modal.Title>
                        <button type="button" className="close" onClick={hideConfirmModal}>&times;</button>
                    </Modal.Header>
                    <Modal.Body>
                        <div>
                            <p>Are you sure you want to delete {deleteHolidayName}?</p>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <button className="popup-button--no" onClick={hideConfirmModal}>No</button>
                        <button className="popup-button--yes" onClick={saveConfirmModal}>Yes</button>
                    </Modal.Footer>
                </Modal>
            </div>
            <div className={showAddPublicHoliday ? "hidden" : ""}>
                <button className="defaultbutton defaultbutton__large form-margin" onClick={showAddNewPublicHoliday} type="button">Add Public Holiday Rule</button>
            </div>
            <div className={showAddPublicHoliday ? "" : "hidden"}>
                <label className="input-group" htmlFor="name">
                    <span className="label">Name</span>
                    <input className="input" type="text" id="name" name="name" value={addPublicHoliday.name} onChange={(e) => handleAddChange(e)}></input>
                </label>
                <span className="label errors textalignleft marginLeft10">{errors["name"]}</span>

                <label className="input-group" htmlFor="holidayType">
                    <span className="label">Type </span>
                    <select className="select" id="holidayType" name="holidayTypeId" value={addPublicHoliday.holidayTypeId} onChange={(e) => handleAddTypeChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {holidayTypes.map(type =>
                            <option key={type.id} value={type.id}>{type.name}</option>
                        )};
                    </select>
                </label>
                <span className="label errors textalignleft marginLeft10">{errors["holidayTypeId"]}</span>

                <div className={addPublicHoliday.holidayTypeId == 1 ? "" : "hidden"}>
                    <label className="input-group" htmlFor="specificDate">
                        <span className="label">Specific Date</span>
                        <input type='date' id="specificDate" name="specificDate" className='input' value={addPublicHoliday.specificDate} onChange={(e) => handleAddChange(e)}></input>
                    </label>
                    <span className="label errors textalignleft marginLeft10">{errors["specificDate"]}</span>
                </div>
                <div className={addPublicHoliday.holidayTypeId > 1 ? "" : "hidden"}>
                    <label className="input-group" htmlFor="day">
                        <span className="label">Day</span>
                        <input type='number' min="-2" max="31" id="day" name="day" className='input' value={addPublicHoliday.day} onChange={(e) => handleAddChange(e)}></input>
                    </label>
                    <span className="label errors textalignleft marginLeft10">{errors["day"]}</span>
                </div>
                <div className={addPublicHoliday.holidayTypeId > 1 && addPublicHoliday.holidayTypeId < 5 ? "" : "hidden"}>
                    <label className="input-group" htmlFor="month">
                        <span className="label">Month</span>
                        <input type='number' min="0" max="12" id="month" name="month" className='input' value={addPublicHoliday.month} onChange={(e) => handleAddChange(e)}></input>
                    </label>
                    <span className="label errors textalignleft marginLeft10">{errors["month"]}</span>
                </div>
                <div className={addPublicHoliday.holidayTypeId == 4 ? "" : "hidden"}>
                    <label className="input-group" htmlFor="week">
                        <span className="label">Week</span>
                        <input type='number' min="0" max="5" id="week" name="week" className='input' value={addPublicHoliday.week} onChange={(e) => handleAddChange(e)}></input>
                    </label>
                    <span className="label errors textalignleft marginLeft10">{errors["week"]}</span>
                </div>
                <div>
                    <button className="defaultbutton defaultbutton__small marginLeftLabel" onClick={save} type="button">Add</button>
                    <button className="defaultbutton defaultbutton__small form-margin" onClick={showAddNewPublicHoliday} type="button">Cancel</button>
                </div>
                <span>Specific Date: when there is no way of calculating the date, e.g. Grand Final Day</span><br></br>
                <span>Same Day and Month: when the holiday always falls on the exact same day and month, e.g. Anzac Day</span><br></br>
                <span>Nearest Weekday: when holiday is on a weekend but observed on the nearest weekday, e.g. Christmas Day</span><br></br>
                <span>Specific Day and Week of Month: when the holiday is on x day in y week, e.g. Labour Day is 2nd Monday in March (Day = 1 for Mon, Month = 3 for March, Week = 2 for 2nd week)</span><br></br>
                <span>Easter: calculates Easter Sunday, for Good Friday use Day = -2, Easter Sunday use Day = 0, Easter Monday use Day = 1</span>
            </div>
            <div>
                <hr></hr>
            </div>
            <div>
                <div className="job__noteGridSize overflowAuto">
                    <table className="table tableColours">
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Day</th>
                                <th>Month</th>
                                <th>Year</th>
                                <th>Week</th>
                                <th></th>
                            </tr>
                        </thead>
                        {holidayRules.map((holiday, index) =>
                            <tbody key={holiday.id}>
                                <tr>
                                    <td className="hidden">{holiday.id}</td>
                                    <td>{holiday.name}</td>
                                    <td>{holiday.type}</td>
                                    <td>{holiday.day}</td>
                                    <td>{holiday.month}</td>
                                    <td>{holiday.year}</td>
                                    <td>{holiday.week}</td>
                                    <td>
                                        <div className="delete--tablecell">
                                            <a className="makeitred" href="#" onClick={(e) => deleteItem(e, holiday.id, index, holiday.name)}>
                                                <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>
            </div>
            <div>
                <span>View actual holiday dates between {' '}
                    <select className="select" id="startYear" name="startYear" value={startYear} onChange={(e) => { clearError(e.target.name); setStartYear(Number(e.target.value)); } } >
                        {holidayYears.map(year =>
                            <option key={year} value={year}>{year}</option>
                        )};
                    </select>
                    {' '} and {' '}
                    <select className="select" id="endYear" name="endYear" value={endYear} onChange={(e) => { clearError(e.target.name); setEndYear(Number(e.target.value)); }} >
                        {holidayYears.map(year =>
                            <option key={year} value={year}>{year}</option>
                        )};
                    </select>
                </span>
                <button className="defaultbutton defaultbutton__small form-margin" onClick={showPublicHolidays} type="button">GO</button>
            </div>
            <div className={holidayDates.length > 0 ? "" : "hidden"}>
                <div className="job__noteGridSize overflowAuto">
                    <table className="table tableColours">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        {holidayDates.map((dates, index) =>
                            <tbody key={index}>
                                <tr>
                                    <td>{dates.name}</td>
                                    <td>{dates.dateDisplay}</td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;


    return <div>
        <h1>Public Holidays</h1>
        {contents}
    </div>;

};

export default PublicHolidayData;